<template>
  <div class="min-h-screen flex flex-col lg:flex-row lg:gap-x-8 xl:gap-x-16">
    <div
      class="flex-1 flex-col justify-center items-center bg-primary-dark m-4 rounded-2xl hidden lg:flex relative"
    >
      <div class="flex flex-col gap-y-6 items-center">
        <img
          src="../assets/chat-polls-logo.png"
          alt="logo"
          class="object-contain h-24"
        />
        <img
          src="../assets/background-circle.png"
          alt="logo"
          class="object-contain h-96 absolute bottom-0 left-4"
        />
        <p
          class="text-white text-center font-poppins text-xl leading-9 font-semibold max-w-md"
        >
          Your quick and easy tool for gathering opinions and making informed
          decisions within Google Chat. Log in and continue collaborating
          effortlessly.
        </p>
      </div>
    </div>
    <div
      class="flex-1 flex flex-col justify-center px-4 sm:px-6 lg:px-0 py-6 sm:py-8 lg:py-8"
    >
      <h1
        class="text-primary-dark-text font-poppins text-2xl sm:text-3xl md:text-4xl lg:text-5xl xl:text-6xl font-bold mb-2 lg:mb-3"
      >
        Sign Up To Chat-Polls
      </h1>
      <form
        @submit.prevent="handleSubmit"
        class="mt-4 lg:mt-6 flex flex-col gap-y-3 lg:gap-y-4 max-w-xl"
      >
        <div v-if="errorMessage" class="text-red-600 font-onest">
          {{ errorMessage }}
        </div>

        <FormInput
          v-model="organizationName"
          label="Organization Name"
          type="text"
          placeholder="Chat-Polls"
          id="organizationName"
        />

        <div class="grid grid-cols-1 sm:grid-cols-2 gap-3 lg:gap-4">
          <div>
            <label
              for="country"
              class="block text-primary-dark-text font-onest text-base lg:text-lg font-medium mb-2 lg:mb-3"
            >
              Country
            </label>
            <select
              v-model="country"
              id="country"
              name="country"
              required
              class="w-full px-3 lg:px-4 py-3 lg:py-4 text-gray-500 font-onest text-sm lg:text-base bg-white border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-all duration-200"
            >
              <option value="" disabled>Select Country</option>
              <option
                v-for="country in countries"
                :key="country"
                :value="country"
              >
                {{ country }}
              </option>
            </select>
          </div>
          <FormInput
            v-model="city"
            label="City"
            type="text"
            placeholder="Dallas"
            id="city"
          />
        </div>

        <FormInput
          v-model="address"
          label="Address"
          type="text"
          placeholder="Dallas, TX 7th Rd 50432"
          id="address"
        />

        <div class="grid grid-cols-1 sm:grid-cols-2 gap-3 lg:gap-4">
          <div>
            <label
              for="industry"
              class="block text-primary-dark-text font-onest text-base lg:text-lg font-medium mb-2 lg:mb-3"
            >
              Industry
            </label>
            <select
              v-model="industry"
              id="industry"
              name="industry"
              required
              class="w-full px-3 lg:px-4 py-3 lg:py-4 text-gray-500 font-onest text-sm lg:text-base bg-white border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-all duration-200"
            >
              <option value="" disabled>Select Industry</option>
              <option
                v-for="industry in industries"
                :key="industry"
                :value="industry"
              >
                {{ industry }}
              </option>
            </select>
          </div>
          <div>
            <label
              for="companyPosition"
              class="block text-primary-dark-text font-onest text-base lg:text-lg font-medium mb-2 lg:mb-3"
            >
              Company Position
            </label>
            <select
              v-model="companyPosition"
              id="companyPosition"
              name="companyPosition"
              required
              class="w-full px-3 lg:px-4 py-3 lg:py-4 text-gray-500 font-onest text-sm lg:text-base bg-white border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-all duration-200"
            >
              <option value="" disabled>Select Position</option>
              <option value="CEO">CEO</option>
              <option value="CTO">CTO</option>
              <option value="CFO">CFO</option>
              <option value="COO">COO</option>
              <option value="Manager">Manager</option>
              <option value="Developer">Developer</option>
              <option value="Designer">Designer</option>
              <option value="Other">Other</option>
            </select>
          </div>
        </div>

        <div class="grid grid-cols-1 sm:grid-cols-2 gap-3 lg:gap-4">
          <FormInput
            v-model="companyEmail"
            label="Email"
            type="email"
            placeholder="info@example.com"
            id="companyEmail"
          />
          <FormInput
            v-model="phoneNumber"
            label="Phone Number"
            type="tel"
            placeholder="+1 234 2233 543"
            id="phoneNumber"
          />
        </div>

        <div
          class="my-3 lg:my-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3"
        >
          <button
            type="submit"
            :disabled="loading"
            class="bg-btn-gradient text-white py-3 px-8 lg:px-12 rounded-md font-onest font-medium text-base lg:text-lg hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 transition-all duration-200 disabled:opacity-50 w-full sm:w-auto"
          >
            {{ loading ? "Signing Up..." : "Sign Up" }}
          </button>

          <!-- Sign In Link -->
          <div>
            <span class="text-gray-600 font-onest text-sm lg:text-base">
              Already have a account?
            </span>
            <router-link
              to="/login"
              class="text-primary hover:text-primary-dark font-medium underline underline-offset-4 text-sm lg:text-base ml-1"
            >
              Sign In
            </router-link>
          </div>
        </div>
      </form>
    </div>
    <AlertMessage
      :message="alertMessage"
      :alertType="alertType ? alertType : 'empty'"
      @close="handleClose"
    />
  </div>
</template>

<script>
import { createOrganization } from "../services/organization";
import countries from "../assets/data/countries";
import industries from "../assets/data/industries";
import AlertMessage from "../components/AlertMessage.vue";
import FormInput from "../components/ui/FormInput.vue";

export default {
  data() {
    return {
      organizationName: "",
      alertMessage: "",
      alertType: "",
      city: "",
      address: "",
      industry: "",
      companyEmail: "",
      companyPosition: "",
      countries,
      industries,
      country: "",
      phoneNumber: "",
      loading: false,
      errorMessage: "",
    };
  },
  components: {
    AlertMessage,
    FormInput,
  },
  methods: {
    validateEmail(email) {
      if (!email) return false;
      const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      return re.test(email.trim());
    },
    validatePhoneNumber(phone) {
      if (!phone) return true; // Phone is optional
      const re = /^[0-9\s+()-]{7,15}$/;
      return re.test(phone.trim());
    },
    handleClose() {
      this.alertMessage = "";
      this.alertType = "";
    },
    async handleSubmit() {
      this.errorMessage = "";
      this.loading = true;

      // Validation
      if (!this.organizationName.trim()) {
        this.errorMessage = "Organization name is required.";
        this.loading = false;
        return;
      }
      if (!this.country.trim()) {
        this.errorMessage = "Country is required.";
        this.loading = false;
        return;
      }
      if (!this.city.trim()) {
        this.errorMessage = "City is required.";
        this.loading = false;
        return;
      }
      if (!this.address.trim()) {
        this.errorMessage = "Address is required.";
        this.loading = false;
        return;
      }
      if (!this.industry.trim()) {
        this.errorMessage = "Industry is required.";
        this.loading = false;
        return;
      }
      if (!this.companyPosition.trim()) {
        this.errorMessage = "Company position is required.";
        this.loading = false;
        return;
      }
      if (!this.validateEmail(this.companyEmail)) {
        this.errorMessage = "Please enter a valid email address.";
        this.loading = false;
        return;
      }
      if (this.phoneNumber && !this.validatePhoneNumber(this.phoneNumber)) {
        this.errorMessage = "Please enter a valid phone number.";
        this.loading = false;
        return;
      }

      const requestData = {
        organization: {
          name: this.organizationName,
          city: this.city,
          address: this.address,
          country: this.country,
          industry: this.industry,
          plan: "paid",
          email: this.companyEmail,
          phoneNumber: this.phoneNumber
            ? parseInt(this.phoneNumber.replace(/\D/g, ""))
            : null,
          image: null, // Removed image upload for now
        },
        user: {
          firstName: "", // Will need to be collected elsewhere or defaulted
          lastName: "", // Will need to be collected elsewhere or defaulted
          email: this.companyEmail, // Using company email as user email
          password: "defaultPassword123!", // Will need proper password handling
          chatProvider: "google",
          roles: [{ name: "ORGANIZATION_ADMIN" }],
        },
      };

      try {
        await createOrganization(JSON.stringify(requestData));
        window.location.href = "https://connect-951707089300.us-east4.run.app";
      } catch (error) {
        const status = error.response?.status;
        const errorMessage =
          error.response?.data?.detail || "Unknown error occurred.";

        if (status === 409) {
          if (errorMessage.includes("Organization name already exists")) {
            this.errorMessage =
              "The organization name already exists. Please choose a different name.";
          } else if (
            errorMessage.includes("Email of organization already exists")
          ) {
            this.errorMessage =
              "The email address already exists for the organization. Please use a different email address.";
          } else if (errorMessage.includes("Email of user already exists")) {
            this.errorMessage =
              "The email address already exists for another user. Please use a different email address.";
          } else {
            this.errorMessage = "There was a conflict contact support.";
          }
        } else {
          this.errorMessage =
            "There was an error submitting the form. Contact support.";
        }
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style scoped>
select {
  background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e");
  background-position: right 0.75rem center;
  background-repeat: no-repeat;
  background-size: 1.5em 1.5em;
  padding-right: 2.5rem !important;
  -webkit-appearance: none;
  -moz-appearance: none;
  appearance: none;
}
</style>
